import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, money, mesi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, FileText, Pencil } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const CAT_LABEL = { pulizie: "Pulizie", lavanderia: "Lavanderia", consumabili: "Consumabili", amenities: "Amenities", manutenzioni: "Manutenzioni", altri: "Altri costi", altri_ricavi: "Altri ricavi" };
const now = new Date();

/** Hook: propone e conferma l'aggiornamento di una voce del Rendiconto (anteprima → conferma). */
export function useReportLink(onApplied) {
  const [prev, setPrev] = useState(null); // {preview, req, opts}

  const propose = useCallback(async (req, opts = {}) => {
    try {
      const { data } = await http.post("/owner-reports/link-cost", { ...req, confirm: false });
      if (!data.found) { if (!opts.silentIfNoReport) toast.info(`${data.message} (${mesi[req.month]} ${req.year}). Salvato solo nel registro.`); return; }
      if (opts.silentIfNoReport && Math.abs(data.amount_after - data.amount_before) < 0.005) return; // niente da proporre
      if (opts.onlyIfLinked && !data.already_linked) return;
      setPrev({ preview: data, req, opts });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  const confirm = async () => {
    try {
      await http.post("/owner-reports/link-cost", { ...prev.req, confirm: true });
      toast.success("Rendiconto aggiornato"); setPrev(null); onApplied?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const p = prev?.preview; const r = prev?.req; const o = prev?.opts || {};
  const dialog = (
    <Dialog open={!!prev} onOpenChange={(op) => !op && setPrev(null)}>
      <DialogContent className="sm:max-w-md" data-testid="report-link-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-[var(--teal-deep)]">{o.title || "Aggiornare anche il Rendiconto?"}</DialogTitle></DialogHeader>
        {p && (
          <div className="space-y-3 text-sm">
            {p.finalized && <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-3 flex gap-2" data-testid="finalized-warning"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><div><b>ATTENZIONE</b><br />Il Rendiconto {mesi[r.month]} {r.year} è già finalizzato ({p.status}). Resterà finalizzato ma i valori ufficiali cambieranno.</div></div>}
            <div className="rounded-xl border border-slate-200 p-3 space-y-1">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Rendiconto collegato · {mesi[r.month]} {r.year}</div>
              {r.use_operative && <div className="flex justify-between text-slate-600"><span>Il registro suggerisce</span><span className="tabular-nums" data-testid="link-operative">{money(p.operative_amount)}</span></div>}
              <div className="flex justify-between"><span>{CAT_LABEL[p.category]} nel Rendiconto</span><b className="tabular-nums" data-testid="link-amount">{money(p.amount_before)} → {money(p.amount_after)}</b></div>
              <div className="flex justify-between"><span>Netto proprietario</span><b className="tabular-nums text-[var(--teal-deep)]" data-testid="link-net">{money(p.owner_result_before)} → {money(p.owner_result_after)}</b></div>
              {p.already_linked && <div className="text-[11px] text-slate-500">Questa voce era già collegata: verrà aggiornata, non sommata due volte.</div>}
            </div>
            <p className="text-[11px] text-slate-500">Decidi tu: il Rendiconto vince finché non confermi.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setPrev(null)} data-testid="link-cancel">{o.soloLabel || "No, solo registro"}</Button>
          <Button className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" onClick={confirm} data-testid="link-confirm">Aggiorna anche Rendiconto</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { propose, dialog };
}

/** Riepilogo mensile: OPERATIVO (override admin) vs ADDEBITATO PROPRIETARIO, differenza, flag Aggiorna Rendiconto. */
export function MonthChargesTable({ category, testid = "month-charges" }) {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // {row, operativo, addebito, updateReport}
  const load = useCallback(async () => {
    const { data } = await http.get("/owner-reports-month-costs", { params: { year, month, category } });
    setRows(data.rows);
  }, [year, month, category]);
  useEffect(() => { load(); }, [load]);
  const { propose, dialog } = useReportLink(load);

  const chargeOf = (r) => (r.charge_amount ?? r.addebitato ?? null);
  const openEdit = (r) => setEdit({ row: r, operativo: String(r.operativo ?? 0), addebito: chargeOf(r) === null ? "" : String(chargeOf(r)), updateReport: !!r.report_id });

  const saveEdit = async () => {
    const r = edit.row; const op = Number(edit.operativo); const ch = edit.addebito === "" ? null : Number(edit.addebito);
    try {
      await http.put("/operative-overrides", { apartment_id: r.apartment_id, year, month, category,
        operative_amount: Math.abs(op - r.operativo_calc) < 0.005 ? null : op, charge_amount: ch });
      toast.success("Registro aggiornato");
      const chargeChanged = ch !== null && (r.addebitato === null || Math.abs(ch - r.addebitato) >= 0.005);
      setEdit(null);
      if (edit.updateReport && r.report_id && chargeChanged) {
        propose({ apartment_id: r.apartment_id, year, month, category, amount: ch, label: `${CAT_LABEL[category]} · addebito dal riepilogo` });
      } else if (!edit.updateReport && chargeChanged) {
        toast.info("Rendiconto NON modificato (flag Aggiorna Rendiconto disattivo)");
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="rounded-2xl p-4 space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display text-lg font-bold text-[var(--teal-deep)]">Resoconto {CAT_LABEL[category]} · {mesi[month]} {year}</div>
          <p className="text-xs text-slate-500">Operativo (registro, sovrascrivibile) e Addebitato proprietario possono essere diversi. Il Rendiconto cambia solo se lo decidi tu.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="w-[130px]" data-testid={`${testid}-month`}><SelectValue /></SelectTrigger>
            <SelectContent>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="w-[95px]"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      {rows.length > 0 && (() => {
        const tot = rows.reduce((a, r) => { const ch = chargeOf(r); return { n: a.n + (r.operativo_count || 0), op: a.op + (r.operativo || 0), ch: a.ch + (ch || 0), rep: a.rep + (r.addebitato || 0) }; }, { n: 0, op: 0, ch: 0, rep: 0 });
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid={`${testid}-resoconto`}>
            {[["Interventi", tot.n, false], ["Costo operativo", tot.op, true], ["Addebitato proprietari", tot.ch, true], ["Differenza / margine", tot.ch - tot.op, true], ["Nei Rendiconti", tot.rep, true]].map(([l, v, m]) => (
              <div key={l} className="rounded-xl border border-[color:var(--pv-mint-soft)] bg-[color:var(--teal-light)]/60 p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--teal-medium)] font-semibold">{l}</div><div className={`font-display text-lg font-extrabold tabular-nums ${l.startsWith("Differenza") && v < 0 ? "text-rose-700" : "text-[var(--teal-deep)]"}`}>{m ? money(v) : v}</div></div>
            ))}
          </div>
        );
      })()}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b border-slate-200 text-[11px] uppercase text-slate-500"><th className="py-2 pr-3">Appartamento</th><th className="pr-3 text-right">Interventi</th><th className="pr-3 text-right">Operativo</th><th className="pr-3 text-right">Addebitato proprietario</th><th className="pr-3 text-right">Differenza</th><th className="pr-3 text-right">Nel Rendiconto</th><th className="pr-3">Stato</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const ch = chargeOf(r);
              const pending = r.report_id && ch !== null && r.addebitato !== null && Math.abs(ch - r.addebitato) >= 0.005;
              return (
                <tr key={r.apartment_id} className="border-b border-slate-100" data-testid={`${testid}-row-${r.apartment_id}`}>
                  <td className="py-2 pr-3 font-medium">{r.apartment_name}</td>
                  <td className="pr-3 text-right tabular-nums text-slate-600">{r.operativo_count || 0}</td>
                  <td className="pr-3 text-right tabular-nums" data-testid={`${testid}-op-${r.apartment_id}`}>{money(r.operativo)}{r.operativo_override && <div className="text-[10px] text-amber-700">override · calcolato {money(r.operativo_calc)}</div>}</td>
                  <td className="pr-3 text-right tabular-nums font-semibold" data-testid={`${testid}-charge-${r.apartment_id}`}>{ch === null ? <span className="text-slate-400">—</span> : money(ch)}</td>
                  <td className={`pr-3 text-right tabular-nums ${ch !== null && Math.abs(ch - r.operativo) >= 0.005 ? "text-amber-700 font-semibold" : "text-slate-400"}`} data-testid={`${testid}-diff-${r.apartment_id}`}>{ch === null ? "—" : (ch - r.operativo >= 0 ? "+" : "") + money(ch - r.operativo)}</td>
                  <td className="pr-3 text-right tabular-nums" data-testid={`${testid}-report-${r.apartment_id}`}>
                    {r.report_id ? <>{money(r.addebitato)}{r.considera === false && <span className="text-[10px] text-slate-400 ml-1">(non addebitato)</span>}
                      {pending && <button className="block ml-auto text-[11px] text-[var(--teal-deep)] underline" data-testid={`${testid}-send-${r.apartment_id}`} onClick={() => propose({ apartment_id: r.apartment_id, year, month, category, amount: ch, label: `${CAT_LABEL[category]} · addebito dal riepilogo` })}>Invia {money(ch)} al Rendiconto</button>}</>
                      : <span className="text-slate-400">nessun rendiconto</span>}
                  </td>
                  <td className="pr-3">{r.report_id ? <Link to={`/rendiconti/${r.report_id}`}><Badge variant="outline" className="capitalize gap-1 border-slate-200"><FileText className="h-3 w-3" />{r.status}</Badge></Link> : null}</td>
                  <td className="pr-1 text-right"><Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(r)} data-testid={`${testid}-edit-${r.apartment_id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-sm" data-testid={`${testid}-edit-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-xl text-[var(--teal-deep)]">{edit?.row.apartment_name} · {CAT_LABEL[category]} · {mesi[month]} {year}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3 text-sm">
              <div><Label>Operativo (€) <span className="text-slate-400 font-normal">calcolato {money(edit.row.operativo_calc)}</span></Label>
                <Input type="number" step="0.01" value={edit.operativo} onChange={(e) => setEdit({ ...edit, operativo: e.target.value })} data-testid={`${testid}-edit-op`} /></div>
              <div><Label>Addebitato al proprietario (€)</Label>
                <Input type="number" step="0.01" value={edit.addebito} onChange={(e) => setEdit({ ...edit, addebito: e.target.value })} data-testid={`${testid}-edit-charge`} />
                {edit.addebito !== "" && <div className="text-xs text-slate-500 mt-1">Differenza: {money(Number(edit.addebito) - Number(edit.operativo || 0))}</div>}</div>
              <div className="flex items-center gap-3 rounded-lg border border-[color:var(--pv-mint-soft)] bg-[color:var(--teal-light)] p-2.5">
                <Switch checked={edit.updateReport} disabled={!edit.row.report_id} onCheckedChange={(v) => setEdit({ ...edit, updateReport: v })} data-testid={`${testid}-edit-flag`} />
                <Label className="text-sm">Aggiorna Rendiconto {edit.row.report_id ? `(attuale ${money(edit.row.addebitato)})` : "(nessun rendiconto)"}</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button><Button className="bg-[var(--teal-deep)]" onClick={saveEdit} data-testid={`${testid}-edit-save`}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {dialog}
    </Card>
  );
}
